import useSWRImmutable from "swr/immutable";
import * as z from "zod/mini";

import type { TimeStamp } from "../../types/TimeStamp";
import type { Store } from "store";

type UseEventImagesInput = {
  transaction?: Store["purchase"]["activeTransactionMap"][string];
};

type EventPart = {
  name: string;
  openTime: TimeStamp;
};

type UseEventImagesOutput = {
  eventImageMap: Record<string, string>;
  eventPartsMap: Record<string, EventPart[]>;
  isLoading: boolean;
};

const timestampSchema = z.object({
  seconds: z.number(),
  nanoseconds: z.number(),
});

const eventRefSchema = z.object({
  defaultImg: z.optional(z.string()),
  parts: z.optional(
    z.array(
      z.object({
        name: z.string(),
        openTime: timestampSchema,
      })
    )
  ),
});

type FetchResult = {
  imageMap: Record<string, string>;
  partsMap: Record<string, EventPart[]>;
};

const fetchEventData = async (
  transaction: Store["purchase"]["activeTransactionMap"][string]
): Promise<FetchResult> => {
  const imageMap: Record<string, string> = {};
  const partsMap: Record<string, EventPart[]> = {};
  const uniqueEventRefs = new Map<
    string,
    FirebaseFirestore.DocumentReference
  >();

  transaction.products?.forEach((product) => {
    if (product.eventRef && product.eid) {
      uniqueEventRefs.set(product.eid, product.eventRef);
    }
  });

  await Promise.all(
    Array.from(uniqueEventRefs.entries()).map(async ([eid, eventRef]) => {
      const eventDoc = await eventRef.get();
      const parseResult = eventRefSchema.safeParse(eventDoc.data());
      if (!parseResult.success) {
        console.error(
          `Failed to parse event data for eid: ${eid}:`,
          parseResult.error
        );
        imageMap[eid] = "";
        partsMap[eid] = [];
        return;
      }
      const eventData = parseResult.data;
      imageMap[eid] = eventData.defaultImg ?? "";

      if (eventData.parts && eventData.parts.length > 0) {
        partsMap[eid] = eventData.parts.map((part) => ({
          name: part.name,
          openTime: part.openTime,
        }));
      } else {
        partsMap[eid] = [];
      }
    })
  );
  return { imageMap, partsMap };
};

export const useEventImages = (
  input: UseEventImagesInput
): UseEventImagesOutput => {
  const { transaction } = input;
  const hasTransaction = transaction !== undefined;

  const { data, isLoading } = useSWRImmutable<FetchResult>(
    hasTransaction ? ["eventData", transaction.orderId] : null,
    hasTransaction ? async () => fetchEventData(transaction) : null
  );

  return {
    eventImageMap: data?.imageMap ?? {},
    eventPartsMap: data?.partsMap ?? {},
    isLoading: hasTransaction ? isLoading : false,
  };
};
